/**
 * ReactionArrows.ts
 * -----------------------------------------------------------------------------
 * Results ▸ Reactions: one arrow per restrained joint, pointing along the
 * resultant F1/F2/F3 direction, length/color scaled by magnitude relative to
 * the case's largest reaction. Built from FEMeshExporter.ExportReactionArrows'
 * JSON (BridgeViewport.setReactionArrows) — a separate overlay from FEScene,
 * not part of the main FE mesh payload, so toggling it never rebuilds the mesh.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Viewport3D } from './Viewport3D';
import { getColor } from './ColorScale';

export interface ReactionArrowsPayload {
  maxMagnitude?: number;
  maxLengthM?: number;   // physical length (view-space m) the largest-magnitude arrow draws at
  positions?: number[];  // [x,y,z, …] per joint, view-space
  vectors?: number[];    // [dx,dy,dz, …] unit direction per joint, view-space
  magnitudes?: number[]; // resultant |F| per joint
}

/** One arrow as a merged, vertex-colored shaft+cone built along +Y then rotated
 *  to `dir` — the same recipe main.ts's XYZ axis triad uses (axisArrowGeom),
 *  generalized here so both can share it. */
export function arrowGeom(dir: THREE.Vector3, length: number, color: THREE.Color): THREE.BufferGeometry {
  const shaftLen = length * 0.8, headLen = length - shaftLen;
  const shaftR = Math.max(length * 0.03, 1e-4), headR = shaftR * 2.4;
  const shaft = new THREE.CylinderGeometry(shaftR, shaftR, shaftLen, 10);
  shaft.translate(0, shaftLen / 2, 0);
  const head = new THREE.ConeGeometry(headR, headLen, 14);
  head.translate(0, shaftLen + headLen / 2, 0);
  const geom = mergeGeometries([shaft, head], false)!;
  shaft.dispose();
  head.dispose();

  geom.applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(
    new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize())));

  const n = geom.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { colors[i * 3] = color.r; colors[i * 3 + 1] = color.g; colors[i * 3 + 2] = color.b; }
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geom;
}

export class ReactionArrows {
  public readonly root = new THREE.Group();
  private mesh: THREE.Mesh | null = null;
  private readonly disposables: Array<THREE.BufferGeometry | THREE.Material> = [];

  constructor(viewport: Viewport3D) {
    this.root.name = 'ReactionArrows';
    this.root.visible = false;
    viewport.scene.add(this.root);
  }

  public set(payload: ReactionArrowsPayload): void {
    this.clear();
    const positions = payload.positions ?? [];
    const vectors = payload.vectors ?? [];
    const magnitudes = payload.magnitudes ?? [];
    const maxMag = payload.maxMagnitude ?? 0;
    const maxLen = payload.maxLengthM ?? 0;
    if (!magnitudes.length || maxMag <= 0 || maxLen <= 0) return;

    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < magnitudes.length; i++) {
      const mag = magnitudes[i];
      if (mag <= 0) continue;
      const dir = new THREE.Vector3(vectors[i * 3], vectors[i * 3 + 1], vectors[i * 3 + 2]);
      if (dir.lengthSq() < 1e-9) continue;

      // Smallest arrow still reads at 15% of the max length — a near-zero
      // reaction shouldn't vanish to an invisible dot.
      const len = Math.max(maxLen * 0.15, (mag / maxMag) * maxLen);
      const [r, g, b] = getColor(mag, 0, maxMag);
      const geom = arrowGeom(dir, len, new THREE.Color(r, g, b));

      // The arrow's head should land ON the joint (it's the force the support
      // applies to the structure, pointing at it) — build along +Y at the
      // origin, then translate its tail back one length along -dir from the joint.
      const joint = new THREE.Vector3(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      const tail = joint.clone().addScaledVector(dir, -len);
      geom.translate(tail.x, tail.y, tail.z);
      parts.push(geom);
    }
    if (!parts.length) return;

    const merged = mergeGeometries(parts, false)!;
    parts.forEach((p) => p.dispose());
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, metalness: 0.05, roughness: 0.7 });
    this.disposables.push(merged, mat);
    this.mesh = new THREE.Mesh(merged, mat);
    this.mesh.name = 'ReactionArrows';
    this.root.add(this.mesh);
    this.root.visible = true;
  }

  public clear(): void {
    if (this.mesh) { this.root.remove(this.mesh); this.mesh = null; }
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.root.visible = false;
  }
}
